import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { SubscriptionData } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function ResumeModal({ open, onClose, sub, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!sub) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/subscription/resume", {
        method: "POST",
        body: { subscriptionId: sub.id },
      });
      if (!res.ok) throw new Error(res.error || "Failed to resume subscription");
      onSuccess("Subscription resumed successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to resume subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800, color: COLORS.green }}>Resume Subscription</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Alert severity="info">
          This will reactivate the subscription. The customer will be charged on the next scheduled date.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Back</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}
          sx={{ bgcolor: COLORS.green, "&:hover": { bgcolor: COLORS.greenDark }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Confirm Resume"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
